import type { ApiError } from '@/lib/api-client';
import type { ErrorEnvelope } from '@/lib/auth-types';

/**
 * P5-4 Commit A — book-scoped envelope evaluator.
 *
 * Consumes the DreamableError envelope shape declared at
 * `pwa/src/lib/auth-types.ts:70-79` (`ErrorEnvelope`) via the
 * `ApiError.envelope` field surfaced by `pwa/src/lib/api-client.ts`.
 * Does NOT touch `pwa/src/lib/errors.ts` — that file's `ErrorVariant`
 * union + `getErrorVariant` handler serves the story-output routes'
 * three-way branch (`technical` / `limitBlock` / `storyNotFound`) and
 * does not model `ui_action` / `cta` / redirect targets. The book
 * flow's envelope evaluation is a different shape and lives here per
 * P5-4 Order R7 (which revises spec §8 T7).
 *
 * The evaluator is `envelope-in / decision-out`. It does NOT render
 * anything, does NOT choose route destinations, does NOT decide
 * navigation. Downstream consumers (a re-entry router in Commit B, a
 * per-route error surface in future book slices) read the returned
 * decision object and render / navigate accordingly.
 */

/**
 * BookErrorKind — the four surface shapes the book flow uses.
 *
 *   `silent`      — the code is NOT an error (see `SILENT_CODES`).
 *                   Downstream should not raise any red affordance.
 *                   Example: `anchor_attempt_stale` per spec §3
 *                   "Wann Rot" — surfaces as a neutral toast + 40 %
 *                   opacity, never as red.
 *   `inline`      — a correctable customer input error. Render inline
 *                   near the offending field. Envelope's
 *                   `ui_action = 'edit_input'` typically maps here.
 *   `screen`      — a screen-level error affordance. Envelope's
 *                   `ui_action = 'stay'` on a non-input error maps
 *                   here. Book flow renders as Bau-Spez §5
 *                   "Bildschirmfehler" (navy text on white with a
 *                   1 px error border), NEVER as a filled red panel.
 *   `redirect`    — the client should navigate (typically to a CTA
 *                   target). Envelope's `ui_action = 'redirect'`
 *                   maps here. `cta.target` supplies the destination.
 */
export type BookErrorKind = 'silent' | 'inline' | 'screen' | 'redirect';

/**
 * Codes that must NOT produce an error affordance per spec §3
 * "Wann Rot" table. Extend cautiously — silent-listing a code hides
 * it from the error UI entirely.
 *
 * `anchor_attempt_stale`: the customer accepted an anchor attempt
 * that the backend has since superseded (fresh regen fired). Spec:
 * "Toastzeile über der Fläche, Bild und Knöpfe auf Deckkraft 40 %.
 * Kein Kundenfehler." Downstream renders neutral, not red.
 */
export const BOOK_SILENT_ERROR_CODES: ReadonlySet<string> = new Set(['anchor_attempt_stale']);

/**
 * BookErrorDecision — the shape the evaluator returns. Consumers
 * project this into UI (inline text, screen banner, redirect
 * navigation, or nothing at all).
 *
 * `errorCode`, `userMessage`, `cta` are surfaced verbatim from the
 * envelope so consumers can render without re-reading the envelope.
 * `redirectTarget` is derived from `cta.target` when `kind` is
 * `redirect`; null otherwise.
 */
export interface BookErrorDecision {
  readonly kind: BookErrorKind;
  readonly errorCode: string | null;
  readonly userMessage: string;
  readonly cta: { readonly label: string; readonly target: string } | null;
  readonly redirectTarget: string | null;
}

/**
 * LAST-RESORT fallback — NOT the default. Sebastian ruling 2026-09-07
 * evening, released as part of the error-messaging wording pass:
 *
 *   "A global fallback that says 'Etwas ist schiefgelaufen' is exactly
 *    the string we spent today removing. Each screen gets the message
 *    that fits its surface."
 *
 * This constant is what `evaluateBookError` returns ONLY when the
 * envelope is null (network failure, malformed JSON, pre-response
 * abort) AND the calling screen has not intercepted with its own
 * surface-appropriate fallback string. Every book screen should
 * prefer a screen-local fallback in its catch — for example,
 * `BestellungScreen.tsx` uses
 * `STRINGS.book.bestellung.checkoutOpenFailedFallback`
 * ("Wir konnten die Bezahlung nicht öffnen. Versuch es nochmal.")
 * instead of this string when the checkout-open call fails without
 * an envelope, because the checkout surface knows what "the thing
 * that just went wrong" was.
 *
 * If you find yourself rendering this string to a customer, that
 * means: (a) an error path exists that no screen has claimed with
 * its own local wording, or (b) evaluateBookError was called with
 * `null` and the caller did not check for that class before rendering.
 * Both are gaps worth filing rather than papering over. The tracker
 * to file under is `[book-error-evaluator-never-adopted]` (adoption
 * tracked over Type B work) until every book screen owns its own
 * surface-appropriate fallback.
 */
const FALLBACK_USER_MESSAGE = 'Etwas ist schiefgelaufen. Bitte versuche es noch einmal.';

/**
 * Evaluate a DreamableError envelope for the book flow.
 *
 * Accepts either an `ApiError` (typical — thrown by `apiFetch`) or a
 * bare `ErrorEnvelope` (for tests or synthesized cases). Returns a
 * `BookErrorDecision` the caller projects into UI.
 *
 * Precedence:
 *   1. If `envelope` is null (network failure, malformed JSON,
 *      pre-response abort) → screen error with fallback message.
 *   2. If `error_code` is in `BOOK_SILENT_ERROR_CODES` → `silent`.
 *   3. If `ui_action === 'redirect'` → `redirect` (target from
 *      `cta.target` when present; fallback to `screen` if `cta` is
 *      null since a redirect without a target is unactionable).
 *   4. If `ui_action === 'edit_input'` → `inline`.
 *   5. Otherwise (`ui_action === 'stay'` or defensive default) →
 *      `screen`.
 *
 * `userMessage` surfaces verbatim from the envelope per spec §8 T6
 * ("Backend-`user_message` wird unverändert angezeigt, nicht
 * umformuliert").
 */
export function evaluateBookError(input: ApiError | ErrorEnvelope | null): BookErrorDecision {
  const envelope: ErrorEnvelope | null =
    input === null ? null : 'envelope' in input ? input.envelope : input;

  if (envelope === null) {
    return {
      kind: 'screen',
      errorCode: null,
      userMessage: FALLBACK_USER_MESSAGE,
      cta: null,
      redirectTarget: null,
    };
  }

  const errorCode = envelope.error_code;
  const userMessage = envelope.user_message ?? FALLBACK_USER_MESSAGE;
  const cta = envelope.cta;

  if (BOOK_SILENT_ERROR_CODES.has(errorCode)) {
    return {
      kind: 'silent',
      errorCode,
      userMessage,
      cta,
      redirectTarget: null,
    };
  }

  switch (envelope.ui_action) {
    case 'redirect':
      // A redirect without a cta.target is unactionable — degrade to
      // a screen error rather than navigating nowhere.
      if (cta === null) {
        return { kind: 'screen', errorCode, userMessage, cta: null, redirectTarget: null };
      }
      return { kind: 'redirect', errorCode, userMessage, cta, redirectTarget: cta.target };
    case 'edit_input':
      return { kind: 'inline', errorCode, userMessage, cta, redirectTarget: null };
    case 'stay':
    default:
      return { kind: 'screen', errorCode, userMessage, cta, redirectTarget: null };
  }
}
